import React, { cloneElement, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router'
import { useEffectOnce, useLanguage } from '@chilecompra/react-kit'
import { useViewport } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { useSecureURL } from '../../modules/hooks/security'
import {
  Root,
  StepInfo,
  StepIcon,
  DesktopSteps,
  DesktopStepsContent,
  DesktopStepsLine,
  DesktopStep,
  DesktopStepInfoTitle,
  DesktopStepInfoSubtitle,
  MobileStep,
  MobileStepInfoTitle,
  MobileStepInfoSubtitle,
  Content,
  Header,
  Brand,
  BrandLogo
} from './WizardLayout.styles'

/**
 * The WizardLayout's component.
 */
const WizardLayout = props => {
  const { defaultStepID, steps } = props
  const navigate = useNavigate()
  const [currentStepID, setCurrentStepID] = useState(defaultStepID)
  const currentStep = useMemo(() => steps.find(step => step.id === currentStepID) || steps[0], [currentStepID, steps])
  const stepsByGroup = useMemo(
    () =>
      steps.reduce((result, step) => {
        const found = result.find(resultStep => resultStep.number === step.number)

        if (found || step.name === undefined) {
          return result
        }

        return result.concat(step)
      }, []),
    [steps]
  )
  const getStepStatus = useCallback(
    number => {
      if (number === currentStep.number) {
        return 'active'
      }

      if (number < currentStep.number) {
        return 'completed'
      }

      return undefined
    },
    [currentStepID]
  )
  const getSecureRoute = useSecureURL()
  const { size } = useViewport()
  const { language } = useLanguage()
  const handleStepChange = stepID => setCurrentStepID(stepID)
  const handleDefaultStep = () => {
    navigate(getSecureRoute(routes.registerWizard), { replace: true })
    setCurrentStepID(defaultStepID)
  }

  useEffectOnce(() => {
    window.history.replaceState({}, null)
  })

  return (
    <Root>
      {!size.isTiny && currentStep.name && (
        <DesktopSteps>
          <DesktopStepsContent>
            <DesktopStepsLine />
            {stepsByGroup.map(step => {
              const status = getStepStatus(step.number)

              return (
                <DesktopStep key={step.id}>
                  <StepInfo>
                    {status && (
                      <DesktopStepInfoTitle>
                        {step.showNumber ? `${language('WizardLayout__step')} ${step.number}` : step.name}
                      </DesktopStepInfoTitle>
                    )}
                    {status && step.showNumber && <DesktopStepInfoSubtitle>{step.name}</DesktopStepInfoSubtitle>}
                  </StepInfo>
                  <StepIcon status={status}>{step.icon}</StepIcon>
                </DesktopStep>
              )
            })}
          </DesktopStepsContent>
        </DesktopSteps>
      )}
      <Content>
        <Header>
          <Brand onClick={handleDefaultStep}>
            <BrandLogo alt="Mercado Público" src="/assets/img/mercadopublico-logo.svg" />
          </Brand>
        </Header>
        {size.isTiny && currentStep.name && (
          <MobileStep>
            <StepIcon status="active">{currentStep.icon}</StepIcon>
            <StepInfo>
              <MobileStepInfoTitle>
                {currentStep.showNumber
                  ? `${language('WizardLayout__step')} ${currentStep.number} ${language('WizardLayout__stepOf')} ${
                      stepsByGroup.length
                    }`
                  : currentStep.name}
              </MobileStepInfoTitle>
              {currentStep.showNumber && <MobileStepInfoSubtitle>{currentStep.name}</MobileStepInfoSubtitle>}
            </StepInfo>
          </MobileStep>
        )}
        {cloneElement(currentStep.component, { onChangeStep: handleStepChange })}
      </Content>
    </Root>
  )
}

WizardLayout.propTypes = {
  defaultStepID: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.node.isRequired,
      icon: PropTypes.node,
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      number: PropTypes.number.isRequired,
      showNumber: PropTypes.bool.isRequired,
      showStep: PropTypes.bool.isRequired
    })
  ).isRequired
}

export default WizardLayout
